// Story 2.2 — mutation hook for POST /api/v1/admin/portfolios.
//
// Class-based `AdminPortfolioComposeError` exposes the AppError's
// `code` discriminator + optional `creator_user_id` / `field` echo so
// the composer page can branch its toast copy + highlight specific
// rows in the wizard.
//
// `Idempotency-Key` header is generated client-side per request so a
// network-retry replay returns the cached envelope from the backend's
// FINANCIAL_MUTATION_PREFIXES middleware (the prefix /api/v1/admin/
// portfolios was added in this story).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";
import type { TierDistribution } from "./useAdminPortfolios";

export type ComposePortfolioCreatorPayload = {
  creator_user_id: string;
  optional_drop_specific_hello_text?: string | null;
  position_in_portfolio?: number;
};

export type ComposePortfolioRequest = {
  title: string;
  theme?: string | null;
  description?: string | null;
  /** Wax-seal base color, lowercase `#rrggbb` (migration 0079). Omitted/
   *  null persists NULL = the component's default bordeaux. The full
   *  pressed-wax shade ramp derives client-side from this one value. */
  seal_color?: string | null;
  kind: "regular" | "special" | "welcome";
  target_creator_count?: number | null;
  creators: ComposePortfolioCreatorPayload[];
  /** ONE set of certificate-tier probabilities for the whole dossier.
   *  Omitted/null persists NULL, which means "use the platform default"
   *  (system_config.tier_distribution_defaults). There is deliberately no
   *  per-creator counterpart on ComposePortfolioCreatorPayload: the
   *  request shape is what makes per-creator odds inexpressible. */
  tier_distribution?: TierDistribution | null;
  /** Open-roster mode: recruiting_status='recruiting', no minimum
   *  creator floor (zero allowed — creators apply later). */
  recruiting?: boolean;
};

export type ComposePortfolioOutcome = {
  portfolio_id: string;
  creator_count: number;
  kind: string;
  recruiting_status: string;
  created_at: string;
};

export type ComposePortfolioResponse = {
  data: ComposePortfolioOutcome;
};

export class AdminPortfolioComposeError extends Error {
  code: string;
  status: number;
  creatorUserId?: string;
  field?: string;
  provided?: number;
  min?: number;
  reason?: string;

  constructor(params: {
    message: string;
    code: string;
    status: number;
    creatorUserId?: string;
    field?: string;
    provided?: number;
    min?: number;
    reason?: string;
  }) {
    super(params.message);
    this.name = "AdminPortfolioComposeError";
    this.code = params.code;
    this.status = params.status;
    this.creatorUserId = params.creatorUserId;
    this.field = params.field;
    this.provided = params.provided;
    this.min = params.min;
    this.reason = params.reason;
  }
}

function generateIdempotencyKey(): string {
  // RFC 4122 v4 — same pattern Story 1.16/1.27/1.30 use.
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `compose-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function postComposePortfolio(
  request: ComposePortfolioRequest,
): Promise<ComposePortfolioResponse> {
  const response = await fetch("/api/v1/admin/portfolios", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": generateIdempotencyKey(),
    },
    body: JSON.stringify(request),
  });
  attachRequestIdToScope(response);

  if (!response.ok) {
    let code = "UNKNOWN";
    let message = `Compose portfolio failed (HTTP ${response.status})`;
    let creatorUserId: string | undefined;
    let field: string | undefined;
    let provided: number | undefined;
    let min: number | undefined;
    let reason: string | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.code) code = raw.error.code;
      if (raw?.error?.message) message = raw.error.message;
      if (raw?.error?.creator_user_id)
        creatorUserId = raw.error.creator_user_id;
      if (raw?.error?.field) field = raw.error.field;
      if (typeof raw?.error?.provided === "number")
        provided = raw.error.provided;
      if (typeof raw?.error?.min === "number") min = raw.error.min;
      if (raw?.error?.reason) reason = raw.error.reason;
    } catch {
      // body not JSON
    }
    throw new AdminPortfolioComposeError({
      message,
      code,
      status: response.status,
      creatorUserId,
      field,
      provided,
      min,
      reason,
    });
  }

  return (await response.json()) as ComposePortfolioResponse;
}

export function useAdminCreatePortfolio() {
  const queryClient = useQueryClient();
  return useMutation<
    ComposePortfolioResponse,
    AdminPortfolioComposeError,
    ComposePortfolioRequest
  >({
    mutationFn: postComposePortfolio,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-portfolios"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio", data.data.portfolio_id],
      });
    },
  });
}
